import axios from 'axios';
import { getApiBaseUrl } from '@/config/env';
import type { ApiResponse } from '@/types/api';
import type {
  IdentityLoginResponse,
  PortalDashboard,
  PortalProfileSummary,
} from '@/types/identity';
import { STORAGE_KEYS } from '@/constants/storage';

const identityClient = axios.create({
  baseURL: getApiBaseUrl(),
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

identityClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(STORAGE_KEYS.IDENTITY_TOKEN);
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

identityClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(STORAGE_KEYS.IDENTITY_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.IDENTITY_SESSION);
      if (!window.location.pathname.startsWith('/identity/login')) {
        window.location.href = '/identity/login';
      }
    }
    return Promise.reject(error);
  },
);

export function setIdentityToken(token: string | null) {
  if (token) {
    localStorage.setItem(STORAGE_KEYS.IDENTITY_TOKEN, token);
  } else {
    localStorage.removeItem(STORAGE_KEYS.IDENTITY_TOKEN);
  }
}

export const identityApi = {
  login: async (identifier: string, password: string, totpCode?: string) => {
    const { data } = await identityClient.post<ApiResponse<IdentityLoginResponse>>(
      '/v1/identity/login',
      { identifier, password, totpCode },
    );
    return data.data!;
  },

  requestMagicLink: async (identifier: string) => {
    await identityClient.post('/v1/identity/magic-link', { identifier });
  },

  verifyMagicLink: async (token: string) => {
    const { data } = await identityClient.post<ApiResponse<IdentityLoginResponse>>(
      '/v1/identity/verify',
      { token },
    );
    return data.data!;
  },

  listPortals: async () => {
    const { data } = await identityClient.get<ApiResponse<PortalProfileSummary[]>>(
      '/v1/identity/portals',
    );
    return data.data ?? [];
  },

  switchPortal: async (profileId: number, portalCode: string) => {
    const { data } = await identityClient.post<ApiResponse<IdentityLoginResponse>>(
      '/v1/identity/switch-portal',
      { profileId, portalCode },
    );
    return data.data!;
  },

  me: async () => {
    const { data } = await identityClient.get<ApiResponse<IdentityLoginResponse>>(
      '/v1/identity/me',
    );
    return data.data!;
  },

  dashboard: async () => {
    const { data } = await identityClient.get<ApiResponse<PortalDashboard>>(
      '/v1/identity/dashboard',
    );
    return data.data!;
  },

  ssoExchange: async (exchangeToken: string) => {
    const { data } = await identityClient.post<ApiResponse<IdentityLoginResponse>>(
      '/v1/identity/sso/exchange',
      { exchangeToken },
    );
    return data.data!;
  },

  // Customer sections (bridged to legacy portal APIs)
  customerTickets: async () => {
    const { data } = await identityClient.get<ApiResponse<import('@/types/desk').DeskTicket[]>>(
      '/v1/identity/portals/customer/tickets',
    );
    return data.data ?? [];
  },

  createCustomerTicket: async (body: { subject: string; description?: string }) => {
    const { data } = await identityClient.post<ApiResponse<import('@/types/desk').DeskTicket>>(
      '/v1/identity/portals/customer/tickets',
      body,
    );
    return data.data!;
  },

  customerTicketDetail: async (id: number) => {
    const { data } = await identityClient.get<ApiResponse<import('@/types/desk').DeskTicketDetail>>(
      `/v1/identity/portals/customer/tickets/${id}`,
    );
    return data.data!;
  },

  addCustomerTicketComment: async (id: number, commentText: string) => {
    await identityClient.post(`/v1/identity/portals/customer/tickets/${id}/comments`, { commentText });
  },

  submitCustomerTicketCsat: async (id: number, rating: number, feedback?: string) => {
    await identityClient.post(`/v1/identity/portals/customer/tickets/${id}/csat`, { rating, feedback });
  },

  customerKbArticles: async (search?: string) => {
    const { data } = await identityClient.get<ApiResponse<import('@/types/desk').DeskKbArticle[]>>(
      '/v1/identity/portals/customer/kb/articles',
      { params: search ? { search } : undefined },
    );
    return data.data ?? [];
  },

  customerProfile: async () => {
    const { data } = await identityClient.get<ApiResponse<Record<string, unknown>>>(
      '/v1/identity/portals/customer/profile',
    );
    return data.data ?? {};
  },

  customerOrders: async () => {
    const { data } = await identityClient.get<ApiResponse<{ orders: Array<Record<string, unknown>> }>>(
      '/v1/identity/portals/customer/orders',
    );
    return data.data?.orders ?? [];
  },

  customerInvoices: async () => {
    const { data } = await identityClient.get<ApiResponse<{ invoices: Array<{
      id: number;
      invoiceNumber: string;
      statusCode: string;
      totalAmount?: number;
      invoiceDate?: string;
    }> }>>('/v1/identity/portals/customer/invoices');
    return data.data?.invoices ?? [];
  },

  customerPayments: async () => {
    const { data } = await identityClient.get<ApiResponse<{ payments: Array<Record<string, unknown>> }>>(
      '/v1/identity/portals/customer/payments',
    );
    return data.data?.payments ?? [];
  },

  vendorPerformance: async () => {
    const { data } = await identityClient.get<ApiResponse<Record<string, unknown>>>(
      '/v1/identity/portals/vendor/performance',
    );
    return data.data ?? {};
  },

  // Vendor sections
  vendorRfqs: async () => {
    const { data } = await identityClient.get<ApiResponse<import('@/api/vendorPortal.api').VendorRfqSummary[]>>(
      '/v1/identity/portals/vendor/rfqs',
    );
    return data.data ?? [];
  },

  vendorPurchaseOrders: async () => {
    const { data } = await identityClient.get<ApiResponse<import('@/api/vendorPortal.api').VendorPoSummary[]>>(
      '/v1/identity/portals/vendor/purchase-orders',
    );
    return data.data ?? [];
  },

  vendorInvoices: async () => {
    const { data } = await identityClient.get<ApiResponse<import('@/api/vendorPortal.api').VendorBillSummary[]>>(
      '/v1/identity/portals/vendor/invoices',
    );
    return data.data ?? [];
  },

  // Employee sections
  employeeProfile: async () => {
    const { data } = await identityClient.get<ApiResponse<import('@/types/hr').Employee360Response>>(
      '/v1/identity/portals/employee/profile',
    );
    return data.data!;
  },

  employeePayrollSummary: async () => {
    const { data } = await identityClient.get<ApiResponse<Record<string, unknown>>>(
      '/v1/identity/portals/employee/payroll',
    );
    return data.data ?? {};
  },

  employeeLeaveBalances: async () => {
    const { data } = await identityClient.get<ApiResponse<Array<{
      id: number;
      leaveTypeId: number;
      balanceDays?: number;
    }>>>(
      '/v1/identity/portals/employee/leave/balances',
    );
    return data.data ?? [];
  },

  // Candidate sections
  candidateOverview: async () => {
    const { data } = await identityClient.get<ApiResponse<Record<string, unknown>>>(
      '/v1/identity/portals/candidate/overview',
    );
    return data.data ?? {};
  },

  candidateInterviews: async () => {
    const { data } = await identityClient.get<ApiResponse<Array<Record<string, unknown>>>>(
      '/v1/identity/portals/candidate/interviews',
    );
    return data.data ?? [];
  },

  candidateOffers: async () => {
    const { data } = await identityClient.get<ApiResponse<Array<Record<string, unknown>>>>(
      '/v1/identity/portals/candidate/offers',
    );
    return data.data ?? [];
  },

  candidateDocuments: async () => {
    const { data } = await identityClient.get<ApiResponse<Array<Record<string, unknown>>>>(
      '/v1/identity/portals/candidate/documents',
    );
    return data.data ?? [];
  },

  candidateOnboarding: async () => {
    const { data } = await identityClient.get<ApiResponse<Array<Record<string, unknown>>>>(
      '/v1/identity/portals/candidate/onboarding',
    );
    return data.data ?? [];
  },

  // Delivery sections
  deliveryAssignments: async () => {
    const { data } = await identityClient.get<ApiResponse<Array<Record<string, unknown>>>>(
      '/v1/identity/portals/delivery/assignments',
    );
    return data.data ?? [];
  },

  confirmDeliveryPod: async (assignmentId: number) => {
    const { data } = await identityClient.post<ApiResponse<Record<string, unknown>>>(
      `/v1/identity/portals/delivery/assignments/${assignmentId}/pod`,
    );
    return data.data!;
  },
};

export function identitySsoAuthorizeUrl(provider: 'google' | 'microsoft'): string {
  const base = getApiBaseUrl();
  const redirect = encodeURIComponent('/identity/oauth/callback');
  return `${base}/v1/auth/sso/${provider}/authorize?redirectAfter=${redirect}`;
}
